import React, { FC, useEffect, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { connect } from 'react-redux'
import { StateType } from '../../../redux/store'
import { EnqueueSnackbarType, showMessage } from '../../common/Helpers'
import DtoolRelationUIBox from './DtoolRelationUIBox'
import { editDtoolRelation } from '../../../redux/appReducer'
import {
  DtoolRelationType,
  DtoolRelationPropsType,
  MaterialType,
  MaterialGroupType,
  HardwareType,
} from '../../../api/appAPI'

interface IProps {
  relation: DtoolRelationType
  hardwares: HardwareType[]
  materials: MaterialType[]
  materialGroups: MaterialGroupType[]
  editDtoolRelation(
    id: number,
    relation: DtoolRelationPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
}

const EditDtoolRelation: FC<IProps> = ({
  relation,
  editDtoolRelation,
  ...props
}) => {
  const idRef = useRef(relation.id)
  const [relationProps, setRelationProps] = useState({
    name: relation.name,
    description: relation.description,
    type: relation.type,
    tags: relation.tags,
    targetId: relation.targetId as number | '',
  })
  const relationPropsRef = useRef(relationProps)

  const [needSave, setNeedSave] = useState(false)
  const needSaveRef = useRef(needSave)

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    idRef.current = relation.id
    setRelationProps({
      name: relation.name,
      description: relation.description,
      type: relation.type,
      tags: relation.tags,
      targetId: relation.targetId,
    })

    setNeedSave(false)
  }, [relation])

  useEffect(() => {
    relationPropsRef.current = relationProps
    needSaveRef.current = needSave
  }, [needSave, relationProps])

  useEffect(
    () => () => {
      if (needSaveRef.current) {
        if (window?.confirm('Save relation?')) {
          handleSaveClick()
        }
      }
    },
    [relation]
  )

  const handleNameChange = (event: React.ChangeEvent<{ value: string }>) => {
    setRelationProps({
      ...relationProps,
      name: event.target.value.replace(/['"]/, ''),
    })
    setNeedSave(true)
  }

  const handleDescriptionChange = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setRelationProps({
      ...relationProps,
      description: event.target.value,
    })
    setNeedSave(true)
  }

  const handleTypeChange = (event: React.ChangeEvent<{ value: string }>) => {
    setRelationProps({
      ...relationProps,
      type: event.target.value as 'hardware' | 'material' | 'materialGroup',
      targetId: '',
    })
    setNeedSave(true)
  }

  const handleTagsChange = (newTags: string[]) => {
    setRelationProps({
      ...relationProps,
      tags: newTags,
    })
    setNeedSave(true)
  }

  const handleTargetIdChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setRelationProps({
      ...relationProps,
      targetId: event.target.value as number,
    })
    setNeedSave(true)
  }

  const handleSaveClick = () => {
    const { name, description, type, tags, targetId } = relationPropsRef.current

    if (!name.trim()) {
      showMessage(enqueueSnackbar, 'Enter relation name')
      return
    }
    if (!targetId) {
      showMessage(enqueueSnackbar, 'Select a target')
      return
    }

    needSaveRef.current = false
    setNeedSave(false)

    const newRelationProps = {
      name,
      description,
      type,
      tags,
      targetId: targetId as number,
    } as DtoolRelationPropsType

    editDtoolRelation(idRef.current, newRelationProps, enqueueSnackbar)
  }

  return (
    <DtoolRelationUIBox
      title={`Edit Dtool Relation`}
      name={{
        value: relationProps.name,
        onChange: handleNameChange,
      }}
      description={{
        value: relationProps.description,
        onChange: handleDescriptionChange,
      }}
      type={{
        value: relationProps.type,
        onChange: handleTypeChange,
      }}
      tags={{
        value: relationProps.tags,
        onChange: handleTagsChange,
      }}
      targetId={{
        value: relationProps.targetId as number,
        onChange: handleTargetIdChange,
      }}
      hardwares={props.hardwares}
      materials={props.materials}
      materialGroups={props.materialGroups}
      onButtonClick={handleSaveClick}
      needSave={needSave}
    />
  )
}

const mapStateToProps = (state: StateType) => ({
  hardwares: state.itemReducer.hardwaresList,
  materials: state.itemReducer.materials,
  materialGroups: state.itemReducer.materialGroups,
})

export default connect(mapStateToProps, { editDtoolRelation })(
  EditDtoolRelation
)
